// Launch boards (contracts/launch-types.md §1–2, content/README "Launch types · …"):
//   board.grid@2 regions, Kakuro clue grids, Crossmath, and grids that take fill / ordered-tap / Yes-No
//   input (LaunchGridView); the Symbol Equation and Hidden-Rule lines and the Balance Scale cards (layouts
//   of board.grid@1 keyed on the type, as content/README asks); board.scene@1, board.hive@1, board.wheel@1.
// Static drawing: no effects, filters or animation; the only effect moves keyboard focus with the active
// fill blank.
import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { shapePath } from "../geometry";
import { GlyphView, puzzleColor, ShapeView } from "../glyphs";
import type {
  Board,
  CellRef,
  Glyph,
  GridBoard,
  GridCell,
  HiveBoard,
  SceneBoard,
  ScenePanel,
  SingleBoard,
  ShapeGlyph,
  StackGlyph,
  TextGlyph,
  WheelBoard,
} from "../types";
import { kindOf } from "../types";
import { cellKey } from "./answers";
import { hiveLayout, regionEdges, regionMap, wheelLayout } from "./geometry";
import { clueLabel, colorName } from "./glyphs";
import { handleTypingKey, rowsBesideGrid, YesNo } from "./inputs";
import styles from "./launch.module.css";
import type { LaunchPlay } from "./play";

const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).join(" ");
const ROW_LETTERS = "ABCDEFGHIJKL";
const clampInt = (v: unknown, min: number, max: number, fallback: number) =>
  Math.min(max, Math.max(min, typeof v === "number" && Number.isFinite(v) ? Math.trunc(v) : fallback));
const clampNum = (v: unknown, min: number, max: number, fallback: number) =>
  Math.min(max, Math.max(min, typeof v === "number" && Number.isFinite(v) ? v : fallback));
const countLabel = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

/**
 * Type-keyed layouts of older components (content/README "Launch types · …" asks for each):
 * equations (N2 with a divider before the question line, N5), balance scales (N3), the cube net's faces
 * (S3), Zip Path's checkpoint discs (L1), Color Match's column headers (A7), Hidden Word's sentence (W5).
 */
export type LaunchLayout = "equations" | "equations-divided" | "scales" | "net" | "zip" | "color-match" | "sentence" | null;

export function launchLayoutFor(type: unknown): LaunchLayout {
  switch (type) {
    case "number.symbol_equation":
      return "equations-divided";
    case "number.hidden_rule":
      return "equations";
    case "number.balance_scale":
      return "scales";
    case "spatial.cube_net":
      return "net";
    case "logic.zip_path":
      return "zip";
    case "speed.color_match":
      return "color-match";
    case "word.hidden_word":
      return "sentence";
    default:
      return null;
  }
}

/** What PuzzleView hands its boards for the launch types. */
export interface LaunchContext {
  play: LaunchPlay | null;
  layout: LaunchLayout;
}

/** The part of PuzzleView's render context a launch grid reads (structurally the same object). */
export interface GridCtx {
  answerCells: Set<string> | null;
  slotFill: Glyph | null;
  play: { selected: Set<string>; count: number; disabled: boolean; revealed: boolean; toggle: (cell: CellRef) => void } | null;
  launch?: LaunchContext | null;
}

const isClue = (g: unknown) => kindOf(g) === "clue";

/**
 * Whether a grid is drawn by this module: a type layout (equations, scales), grid@2 regions, Kakuro clue
 * squares, or a fill / ordered-tap / Yes-No answer that plays on the grid.
 */
export function wantsLaunchGrid(board: GridBoard, launch: LaunchContext | null | undefined): boolean {
  if (launch?.layout === "net" || launch?.layout === "zip") return true;
  if (launch?.layout && equationShape(board, launch.layout)) return true;
  if (Array.isArray(board.regions) && board.regions.length > 0) return true;
  if ((Array.isArray(board.cells) ? board.cells : []).some((c) => isClue(c?.glyph))) return true;
  const play = launch?.play;
  if (play?.kind === "fill" || play?.kind === "sequence") return true;
  return play?.kind === "rows" && rowsBesideGrid(board, play.rows!.count);
}

/** The launch grid for `board` (call only when `wantsLaunchGrid`). */
export function LaunchGridBoard({ board, ctx }: { board: GridBoard; ctx: GridCtx }) {
  const layout = ctx.launch?.layout ?? null;
  if (layout === "scales" && equationShape(board, layout)) return <ScalesView board={board} ctx={ctx} />;
  if (layout && layout !== "scales" && equationShape(board, layout)) return <EquationLinesView board={board} ctx={ctx} divided={layout === "equations-divided"} />;
  if (layout === "color-match") {
    const split = splitHeaderRow(board);
    if (split) return <LaunchGridView board={split.body} ctx={ctx} headerTexts={split.headers} />;
  }
  return <LaunchGridView board={board} ctx={ctx} />;
}

/**
 * Color Match (board 18 A7) carries its two column headers as the grid's first row of label
 * text (the server translates them). Split that row off so the rows and their Yes/No line up.
 */
function splitHeaderRow(board: GridBoard): { headers: string[]; body: GridBoard } | null {
  const rows = Math.trunc(Number(board.rows));
  const cols = Math.trunc(Number(board.cols));
  if (!(rows > 1 && cols > 0) || !Array.isArray(board.cells) || board.cells.length < rows * cols) return null;
  const head = board.cells.slice(0, cols);
  const texts = head.map((cell) => {
    const g = cell?.glyph as { kind?: string; value?: unknown; style?: string } | undefined;
    return g && g.kind === "text" && g.style === "label" && typeof g.value === "string" ? g.value : null;
  });
  if (texts.some((t) => t === null)) return null;
  return { headers: texts as string[], body: { ...board, rows: rows - 1, cells: board.cells.slice(cols) } };
}

// ---------------------------------------------------------------- spoken glyphs

const OPERATOR_WORDS: Record<string, string> = {
  "+": "plus",
  "-": "minus",
  "−": "minus",
  "×": "times",
  x: "times",
  "÷": "divided by",
  "/": "divided by",
  "=": "equals",
  "≠": "does not equal",
  ">": "is more than",
  "<": "is less than",
  "▸": "gives",
  "→": "gives",
};
const OPERATORS = new Set(Object.keys(OPERATOR_WORDS));
const textValue = (g: unknown) => (kindOf(g) === "text" ? String((g as TextGlyph).value ?? "").trim() : "");
const isOperatorText = (g: unknown) => kindOf(g) === "text" && OPERATORS.has(textValue(g));

/** How a screen reader hears a glyph in a line: "2", "plus", "coral circle", "missing number". */
export function spokenGlyph(g: unknown): string {
  switch (kindOf(g)) {
    case "text": {
      const v = textValue(g);
      return OPERATOR_WORDS[v] ?? v;
    }
    case "shape": {
      const s = g as ShapeGlyph;
      return `${colorName(s.color)} ${String(s.shape ?? "shape")}`;
    }
    case "stack":
      return (Array.isArray((g as StackGlyph).items) ? (g as StackGlyph).items : []).map(spokenGlyph).filter(Boolean).join(" ");
    case "slot":
      return "missing";
    case "empty":
      return "";
    default:
      return kindOf(g);
  }
}

// ---------------------------------------------------------------- equation lines (N2 Symbol Equation, N5 Hidden-Rule Riddle)

/** Rows of 3 or 5 cells whose odd cells are operators or relations: the equation-line and scale encodings. */
function equationShape(board: GridBoard, layout: LaunchLayout): boolean {
  if (layout !== "equations" && layout !== "equations-divided" && layout !== "scales") return false;
  const rows = clampInt(board.rows, 1, 12, 0);
  const cols = clampInt(board.cols, 1, 12, 0);
  const cells = Array.isArray(board.cells) ? board.cells : [];
  if (!rows || (layout === "scales" ? cols !== 3 : cols !== 3 && cols !== 5) || cells.length < rows * cols) return false;
  return cells.slice(0, rows * cols).every((c, i) => (i % cols) % 2 === 0 || isOperatorText(c?.glyph));
}

/** One glyph set inline at text size (a number, an operator, a symbol, a stack, the dashed slot). */
function InlineGlyph({ glyph, ctx, weight = false }: { glyph: Glyph | undefined; ctx: GridCtx; weight?: boolean }): ReactNode {
  switch (kindOf(glyph)) {
    case "text": {
      const t = glyph as TextGlyph;
      const v = String(t.value ?? "");
      if (isOperatorText(t)) return <span className={styles.lineOp}>{v}</span>;
      if (weight && /^\d+$/.test(v.trim())) return <span className={styles.weight}>{v}</span>;
      return (
        <span className={styles.lineNumber} style={t.color && t.color !== "ink" ? { color: puzzleColor(t.color) } : undefined}>
          {v}
        </span>
      );
    }
    case "shape":
      return (
        <span className={cx(styles.lineShape, weight && styles.lineShapeSmall)}>
          <ShapeView glyph={glyph as ShapeGlyph} radius={44} />
        </span>
      );
    case "stack":
      return (
        <span className={styles.lineStack}>
          {((glyph as StackGlyph).items ?? []).map((g, i) => (
            <InlineGlyph key={i} glyph={g} ctx={ctx} weight={weight} />
          ))}
        </span>
      );
    case "slot":
      return ctx.slotFill ? (
        <span className={cx(styles.lineSlot, weight && styles.lineSlotSmall)} data-filled aria-label="missing (answer revealed)">
          {kindOf(ctx.slotFill) === "text" ? (
            <span className={styles.lineSlotValue}>{textValue(ctx.slotFill)}</span>
          ) : (
            <span className={styles.lineSlotInner}>
              <GlyphView glyph={ctx.slotFill} />
            </span>
          )}
        </span>
      ) : (
        <span className={cx(styles.lineSlot, weight && styles.lineSlotSmall)} aria-label="missing">
          ?
        </span>
      );
    case "empty":
    case "undefined":
      return null;
    default:
      return (
        <span className={styles.lineShape}>
          <GlyphView glyph={glyph} />
        </span>
      );
  }
}

/**
 * Lines of an equation or a hidden rule, set like the boards (14 N2, N5): no tiles, numbers large,
 * operators muted, symbols solid, the asked value in the dashed accent slot. Columns line up. Symbol
 * Equation has a dashed divider above its question line (the last row). Each line reads as a sentence
 * ("coral circle plus coral circle equals violet square").
 */
function EquationLinesView({ board, ctx, divided }: { board: GridBoard; ctx: GridCtx; divided: boolean }) {
  const rows = clampInt(board.rows, 1, 12, 1);
  const cols = clampInt(board.cols, 1, 12, 3);
  const cells = board.cells;
  const line = (r: number) => cells.slice(r * cols, r * cols + cols);
  return (
    <div className={styles.lines} style={{ "--cols": cols } as CSSProperties} data-layout="equations">
      {Array.from({ length: rows }, (_, r) => {
        const spoken = line(r).map((c) => spokenGlyph(c?.glyph)).filter(Boolean).join(" ");
        const last = r === rows - 1;
        return [
          divided && last && rows > 1 ? <div key={`d${r}`} className={styles.lineDivider} aria-hidden /> : null,
          <div key={r} className={styles.line} role="img" aria-label={last && rows > 1 ? `Question: ${spoken}` : spoken} data-line={r}>
            {line(r).map((c, i) => (
              <span key={i} className={styles.lineCell} data-op={i % 2 === 1 || undefined}>
                <InlineGlyph glyph={c?.glyph} ctx={ctx} />
              </span>
            ))}
          </div>,
        ];
      })}
    </div>
  );
}

/**
 * Balance scales (board 14 N3): one card per row, "Scale 1", "Scale 2", …, the last one ("Find") in a
 * dashed accent card. Each pan carries its items (symbols, and numbers as white weight blocks) on a pan
 * line; a beam and post join the pans. Each scale reads "Scale 1: mint triangle, mint triangle balances
 * violet square, …".
 */
function ScalesView({ board, ctx }: { board: GridBoard; ctx: GridCtx }) {
  const rows = clampInt(board.rows, 1, 12, 1);
  const cells = board.cells;
  const items = (g: Glyph | undefined) =>
    kindOf(g) === "stack" ? ((g as StackGlyph).items ?? []).map(spokenGlyph).filter(Boolean).join(", ") : spokenGlyph(g);
  return (
    <div className={styles.scales} data-layout="scales">
      {Array.from({ length: rows }, (_, r) => {
        const left = cells[r * 3]?.glyph;
        const right = cells[r * 3 + 2]?.glyph;
        const find = r === rows - 1 && rows > 1;
        const name = find ? "Find" : `Scale ${r + 1}`;
        return (
          <div key={r} className={styles.scale} data-find={find || undefined} role="img" aria-label={`${name}: ${items(left)} balances ${items(right)}`}>
            <span className={styles.scaleName} aria-hidden>
              {name}
            </span>
            <div className={styles.scaleBody} aria-hidden>
              <div className={styles.pan}>
                <div className={styles.panItems}>
                  <InlineGlyph glyph={left} ctx={ctx} weight />
                </div>
              </div>
              <div className={styles.post} />
              <div className={styles.pan}>
                <div className={styles.panItems}>
                  <InlineGlyph glyph={right} ctx={ctx} weight />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- launch grid

type CellKind = "blank" | "clue" | "bare" | "block" | "given" | "empty" | "plain" | "face" | "checkpoint";

/**
 * Grids for the launch types, as one CSS grid of square cells plus one SVG of region outlines:
 *
 * - board.grid@2 regions (KenKen cages, Queens regions): cells touch, thin lines inside a region, thick
 *   outlines between regions (ink, or black between coloured regions as on board 16 L5), a region's
 *   colour as a dark tint on its cells, its label small at its top-left cell ("6×" in ember).
 * - Kakuro: clue squares (glyph.clue@1) and blocks on dark tiles, the white squares lighter.
 * - Crossmath: numbers on tiles, operators and `=` bare in narrower rows and columns, blanks dashed.
 * - answer.fill: tap a blank (or move to it with the arrow keys), then type; the active blank has the
 *   accent ring, entries are in the accent.
 * - answer.sequence: tap cells in order; each tap shows its number on an accent tile (board 17 M3b/M5b);
 *   the tappable subset (`answer.cells`) is marked violet.
 * - answer.rows: a Yes/No pair beside each row (board 18 A7).
 * - answer.cellSet on a grid@2: toggles, as on any grid.
 * Cell names are the grid@1 ones (column letter, row number: "B3").
 */
function LaunchGridView({ board, ctx, headerTexts }: { board: GridBoard; ctx: GridCtx; headerTexts?: string[] }) {
  const rows = clampInt(board.rows, 1, 12, 4);
  const cols = clampInt(board.cols, 1, 12, 4);
  const cells = Array.isArray(board.cells) ? board.cells : [];
  const at = (r: number, c: number): GridCell => cells[r * cols + c] ?? {};
  const launch = ctx.launch?.play ?? null;
  const fill = launch?.kind === "fill" ? launch.fill : null;
  const seq = launch?.kind === "sequence" ? launch.sequence : null;
  const rowsPlay = launch?.kind === "rows" && rowsBesideGrid(board, launch.rows!.count) ? launch : null;
  const cellSet = ctx.play;
  const { byCell, list } = regionMap(board.regions, rows, cols);
  const contiguous = list.length > 0;
  const colored = list.some((r) => r.region.color);
  const kakuro = cells.some((c) => isClue(c?.glyph));
  const anyFlagged = cells.some((c) => c?.interactive);

  const layout = ctx.launch?.layout ?? null;
  const kindAt = (r: number, c: number): CellKind => {
    const cell = at(r, c);
    if (fill?.index.has(cellKey(r, c))) return "blank";
    // Cube Net (board 15 S3): a face is solid colour with its symbol dark; any other square is nothing.
    if (layout === "net") return cell.fill ? "face" : "empty";
    // Zip Path (board 16 L1): the numbered checkpoints are white discs.
    if (layout === "zip" && kindOf(cell.glyph) === "text") return "checkpoint";
    if (isClue(cell.glyph)) return "clue";
    const g = cell.glyph;
    if (contiguous) return g && kindOf(g) !== "empty" ? "given" : "empty";
    if (isOperatorText(g) && !cell.fill && !cell.interactive) return "bare";
    // Nothing here: a Kakuro block, a Crossmath junction (bare), else a plain tile (Order Tap, Tap Order).
    if (!g || kindOf(g) === "empty") return kakuro ? "block" : fill ? "bare" : "plain";
    return "given";
  };
  // Crossmath: rows and columns holding only operators and junctions are narrower (board 14 N7).
  const narrowCol = (c: number) => !contiguous && !kakuro && Array.from({ length: rows }, (_, r) => kindAt(r, c)).every((k) => k === "bare");
  const narrowRow = (r: number) => !contiguous && !kakuro && Array.from({ length: cols }, (_, c) => kindAt(r, c)).every((k) => k === "bare");
  const colW = Array.from({ length: cols }, (_, c) => (narrowCol(c) ? 0.5 : 1));
  const rowH = Array.from({ length: rows }, (_, r) => (narrowRow(r) ? 0.5 : 1));
  const sumW = colW.reduce((a, b) => a + b, 0);
  const sumH = rowH.reduce((a, b) => a + b, 0);

  const tappableAt = (r: number, c: number): boolean => {
    const key = cellKey(r, c);
    if (fill) return fill.index.has(key);
    if (seq) return seq.tappable ? seq.tappable.has(key) : kindAt(r, c) !== "clue";
    if (cellSet) return anyFlagged ? Boolean(at(r, c).interactive) : kindAt(r, c) !== "clue";
    return false;
  };
  const live = Boolean((launch && launch.interactive) || cellSet);
  const locked = launch ? launch.locked || !launch.interactive : Boolean(cellSet?.disabled);
  const tappableKeys: string[] = [];
  if (live) for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) if (tappableAt(r, c)) tappableKeys.push(cellKey(r, c));

  const gridRef = useRef<HTMLDivElement>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const activeKey = fill && launch && launch.active >= 0 && fill.cells[launch.active] ? cellKey(fill.cells[launch.active]![0], fill.cells[launch.active]![1]) : null;
  const rovingKey = (fill && activeKey) || (focusKey && tappableKeys.includes(focusKey) ? focusKey : (tappableKeys[0] ?? null));

  // Typing moves the active blank on (KenKen, Kakuro): keep keyboard focus with it.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || !activeKey || !grid.contains(document.activeElement)) return;
    const el = grid.querySelector<HTMLElement>(`[data-cell="${activeKey}"]`);
    if (el && el !== document.activeElement) el.focus();
  }, [activeKey]);

  const move = (from: string, dr: number, dc: number) => {
    let [r, c] = from.split(",").map(Number) as [number, number];
    for (let step = 0; step < Math.max(rows, cols); step++) {
      r += dr;
      c += dc;
      if (r < 0 || c < 0 || r >= rows || c >= cols) return;
      const key = cellKey(r, c);
      if (tappableKeys.includes(key)) {
        setFocusKey(key);
        if (fill) launch!.setActive(fill.index.get(key)!);
        gridRef.current?.querySelector<HTMLElement>(`[data-cell="${key}"]`)?.focus();
        return;
      }
    }
  };
  const onKeyDown = (e: KeyboardEvent<HTMLElement>, r: number, c: number) => {
    const delta: Record<string, [number, number]> = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] };
    const d = delta[e.key];
    if (d) {
      e.preventDefault();
      move(cellKey(r, c), d[0], d[1]);
      return;
    }
    if (fill && launch) {
      const i = fill.index.get(cellKey(r, c))!;
      const handled = handleTypingKey(e, { ...launch, type: (k) => launch.type(k, i), erase: () => launch.erase(i) }, fill.negative);
      if (handled) e.preventDefault();
    } else if (seq && launch && (e.key === "Backspace" || e.key === "Delete")) {
      e.preventDefault();
      launch.undo();
    }
  };
  const activate = (r: number, c: number) => {
    const key = cellKey(r, c);
    if (fill && launch) launch.setActive(fill.index.get(key)!);
    else if (seq && launch) launch.tap([r, c]);
    else if (cellSet) cellSet.toggle([r, c]);
  };

  // Cell names as on iOS and board 16: "Row B, column 3" when the grid shows its row letters, else
  // "Row 2, column 3"; they count the squares only (Crossmath's operator rows and columns are skipped).
  const wideBefore = (weights: number[], i: number) => weights.slice(0, i).filter((w) => w === 1).length;
  const rowName = (r: number) => (board.labels ? (ROW_LETTERS[wideBefore(rowH, r)] ?? `R${r + 1}`) : String(wideBefore(rowH, r) + 1));
  const name = (r: number, c: number) => `Row ${rowName(r)}, column ${wideBefore(colW, c) + 1}`;
  const labelAt = new Map(list.filter((x) => x.anchor && x.region.label).map((x) => [cellKey(x.anchor![0], x.anchor![1]), String(x.region.label)]));
  const tapOrder = new Map((seq?.taps ?? []).map((cell, i) => [cellKey(cell[0], cell[1]), i + 1]));
  const shownOrder = new Map((seq?.shown ?? []).map((cell, i) => [cellKey(cell[0], cell[1]), i + 1]));

  const tiles: ReactNode[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const key = cellKey(r, c);
      const cell = at(r, c);
      const kind = kindAt(r, c);
      const regionIndex = byCell.get(key);
      const region = regionIndex !== undefined ? list.find((x) => x.index === regionIndex)?.region : undefined;
      const style: CSSProperties & Record<`--${string}`, string> = {};
      const data0: Record<string, boolean> = {};
      const fillColor = cell.fill ?? region?.color;
      if (fillColor) style["--cell-fill"] = puzzleColor(fillColor);
      if (fillColor) data0["data-fill"] = true;
      const regionLabel = labelAt.get(key);
      const parts: string[] = [name(r, c)];
      if (regionLabel) parts.push(`cage ${regionLabel}`);
      else if (region?.color) parts.push(`${colorName(region.color)} region`);
      let content: ReactNode = null;
      const data: Record<string, string | boolean | undefined> = { "data-cell": key, "data-kind": kind, ...data0 };

      if (kind === "clue") {
        content = <GlyphView glyph={cell.glyph} />;
        parts.splice(0, parts.length, `${name(r, c)}, ${clueLabel(cell.glyph as never)}`);
      } else if (kind === "blank" && fill) {
        const i = fill.index.get(key)!;
        const mine = fill.values[i] ?? null;
        const correct = fill.shown?.[i] ?? null;
        const value = fill.shown ? correct : mine;
        const wrong = fill.shown !== null && launch!.interactive && mine !== null && mine !== correct;
        if (value !== null) content = <span className={styles.entry}>{value}</span>;
        data["data-active"] = !locked && live && key === activeKey ? true : undefined;
        data["data-entry"] = value !== null ? true : undefined;
        data["data-answer"] = fill.shown ? true : undefined;
        data["data-wrong"] = wrong || undefined;
        parts.push(fill.shown ? `${correct ?? "blank"}${wrong ? `, you entered ${mine}` : ""}` : mine !== null ? String(mine) : "blank");
      } else {
        if (kind === "checkpoint") {
          content = <span className={styles.checkpoint}>{textValue(cell.glyph)}</span>;
          parts.push(`checkpoint ${textValue(cell.glyph)}`);
        } else if (kind === "face") {
          parts.push(`${colorName(cell.fill)} face${cell.glyph && kindOf(cell.glyph) !== "empty" ? `, ${spokenGlyph(cell.glyph).replace(/^white /, "")}` : ""}`);
          content = cell.glyph ? (
            <div className={styles.cellGlyph} data-face-symbol>
              <GlyphView glyph={cell.glyph} />
            </div>
          ) : null;
        } else if (rowsPlay && kindOf(cell.glyph) === "text") {
          // Color Match words (board 18 A7): bold capitals in their ink, sized for a row, not a square.
          const w = cell.glyph as TextGlyph;
          content = (
            <span className={styles.rowWord} style={{ color: puzzleColor(w.color) }}>
              {textValue(w)}
            </span>
          );
        } else if (kind === "bare" && isOperatorText(cell.glyph)) {
          // Crossmath's operators and "=" sit bare and muted, sized for their narrow track.
          content = <span className={styles.gridOp}>{textValue(cell.glyph)}</span>;
        } else if (cell.glyph && kindOf(cell.glyph) !== "empty") {
          const glyph = kindOf(cell.glyph) === "slot" && ctx.slotFill ? ctx.slotFill : cell.glyph;
          content = (
            <div className={styles.cellGlyph}>
              <GlyphView glyph={glyph} />
            </div>
          );
          if (kindOf(cell.glyph) === "slot") data["data-slot"] = !ctx.slotFill || undefined;
        }
        if (cell.lit) {
          data["data-lit"] = true;
          parts.push("lit");
        }
      }

      if (seq) {
        const n = tapOrder.get(key);
        const shownN = shownOrder.get(key);
        const marked = seq.tappable?.has(key) ?? false;
        if (marked) data["data-marked"] = true;
        if (seq.shown) {
          if (shownN) {
            data["data-answer"] = true;
            content = <span className={styles.order}>{shownN}</span>;
            parts.push(`step ${shownN}`);
          }
          if (n && n !== shownN && launch!.interactive) {
            data["data-wrong"] = true;
            parts.push(`you tapped it ${n}${n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th"}`);
          }
        } else if (n) {
          data["data-tapped"] = true;
          content = <span className={styles.order}>{n}</span>;
          parts.push(`tap ${n}`);
        } else if (marked) parts.push("marked");
      }
      if (cellSet) {
        const selected = cellSet.selected.has(key);
        const isAnswer = ctx.answerCells?.has(key) ?? false;
        if (selected) data["data-selected"] = true;
        if (isAnswer) data["data-answer"] = true;
        if (cellSet.revealed && ctx.answerCells && selected && !isAnswer) data["data-wrong"] = true;
        if (isAnswer) parts.push("answer");
      } else if (ctx.answerCells?.has(key)) {
        data["data-answer"] = true;
        parts.push("answer");
      }

      const inner = (
        <>
          {regionLabel ? (
            <span className={styles.regionLabel} aria-hidden>
              {regionLabel}
            </span>
          ) : null}
          {content}
        </>
      );
      const label = parts.join(", ");
      const place = { gridRow: r + 1, gridColumn: c + 1 };
      if (live && tappableAt(r, c)) {
        const pressed = seq ? tapOrder.has(key) : cellSet ? cellSet.selected.has(key) : undefined;
        tiles.push(
          <button
            key={key}
            type="button"
            className={styles.cell}
            {...data}
            style={{ ...style, ...place }}
            aria-label={label}
            aria-pressed={pressed}
            aria-current={fill && key === activeKey ? true : undefined}
            aria-disabled={locked || undefined}
            tabIndex={rovingKey === key ? 0 : -1}
            onFocus={() => {
              setFocusKey(key);
              if (fill && launch && key !== activeKey) launch.setActive(fill.index.get(key)!);
            }}
            onClick={() => !locked && activate(r, c)}
            onKeyDown={(e) => !locked && onKeyDown(e, r, c)}
          >
            {inner}
          </button>,
        );
      } else {
        tiles.push(
          <div
            key={key}
            className={styles.cell}
            {...data}
            style={{ ...style, ...place }}
            role={kind === "bare" && !content ? undefined : "img"}
            aria-label={kind === "bare" ? (content ? spokenGlyph(cell.glyph) : undefined) : label}
            aria-hidden={kind === "bare" && !content ? true : undefined}
          >
            {inner}
          </div>,
        );
      }
    }
  }

  const edges = contiguous ? regionEdges(byCell, rows, cols) : null;
  const unitsLabel =
    fill && live
      ? `Grid, ${rows} by ${cols}. Choose a blank with the arrow keys or a tap, then type its number.`
      : seq && live
        ? `Grid, ${rows} by ${cols}. Arrow keys move, Space or Enter taps${seq.stopOnError ? "" : ", Backspace takes back the last tap"}.`
        : cellSet
          ? `Grid, ${rows} by ${cols}. Arrow keys move, Space or Enter toggles a square.`
          : undefined;
  const grid = (
    <div
      ref={gridRef}
      className={styles.grid}
      data-contiguous={contiguous || undefined}
      data-colored={colored || undefined}
      data-kakuro={kakuro || undefined}
      data-net={layout === "net" || undefined}
      data-play={live || undefined}
      role={unitsLabel ? "group" : undefined}
      aria-label={unitsLabel}
      style={
        {
          "--cols": cols,
          "--sum-w": sumW,
          gridTemplateColumns: colW.map((w) => `minmax(0, ${w}fr)`).join(" "),
          gridTemplateRows: rowH.map((h) => `minmax(0, ${h}fr)`).join(" "),
          aspectRatio: `${sumW} / ${sumH}`,
        } as CSSProperties
      }
    >
      {tiles}
      {edges ? (
        <svg className={styles.regions} viewBox={`0 0 ${cols} ${rows}`} preserveAspectRatio="none" aria-hidden data-regions>
          <path d={edges.thin} className={styles.regionThin} vectorEffect="non-scaling-stroke" />
          <path d={edges.thick} className={styles.regionThick} vectorEffect="non-scaling-stroke" />
        </svg>
      ) : null}
    </div>
  );

  const labelled = board.labels ? (
    <div className={styles.labelled} style={{ "--cols": cols, "--rows": rows } as CSSProperties}>
      <span />
      <div className={styles.colLabels} aria-hidden>
        {Array.from({ length: cols }, (_, c) => (
          <span key={c}>{c + 1}</span>
        ))}
      </div>
      <div className={styles.rowLabels} aria-hidden>
        {Array.from({ length: rows }, (_, r) => (
          <span key={r}>{ROW_LETTERS[r] ?? r + 1}</span>
        ))}
      </div>
      {grid}
    </div>
  ) : (
    grid
  );

  const headers =
    layout === "color-match" && cols === 2 ? (
      <div className={styles.colHeads} aria-hidden>
        {(headerTexts ?? ["Read the word", "Look at the ink"]).map((h, i) => (
          <span key={i}>{h}</span>
        ))}
      </div>
    ) : null;
  if (!rowsPlay) {
    return headers ? (
      <div className={styles.headed}>
        {headers}
        {labelled}
      </div>
    ) : (
      labelled
    );
  }
  // Color Match (board 18 A7): each row's Yes / No sits beside it, the column headers above.
  return (
    <div className={styles.rowsBoard} style={{ "--rows": rows } as CSSProperties} data-headed={headers ? true : undefined}>
      {headers}
      <div className={styles.rowsGrid}>{labelled}</div>
      <div className={styles.rowsColumn}>
        {Array.from({ length: rows }, (_, r) => (
          <YesNo
            key={r}
            play={rowsPlay}
            row={r}
            label={`Row ${r + 1}: ${Array.from({ length: cols }, (_, c) => spokenGlyph(at(r, c).glyph)).filter(Boolean).join(", ")}`}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Hidden Word (board 20 W5)

/** A single board holding one sentence (Hidden Word): true when it can be set as a quoted sentence. */
export function isSentenceBoard(board: Board | undefined): board is SingleBoard {
  return kindOf(board) === "single" && kindOf((board as SingleBoard).glyph) === "text";
}

/**
 * Hidden Word (board 20 W5): the sentence set large in quotes on a card, no tiles (tiles would give the
 * breaks away), the caption beneath in mono capitals.
 */
export function SentenceBoardView({ board }: { board: SingleBoard }) {
  const raw = textValue(board.glyph);
  const sentence = /^[“"]/.test(raw) ? raw : `“${raw}”`;
  return (
    <div className={styles.sentenceCard}>
      <p className={styles.sentence}>{sentence}</p>
      {board.caption ? <span className={styles.wordCaption}>{board.caption}</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------- board.scene@1

const SCENE_W = 1000;
const SCENE_GAP = 28;

const sceneLabels = (board: SceneBoard): [string, string] => {
  const l = Array.isArray(board.labels) ? board.labels : [];
  const at = (i: number) => (typeof l[i] === "string" && l[i]!.trim() ? l[i]!.trim() : `Scene ${i + 1}`);
  return [at(0), at(1)];
};
const scenePanels = (board: SceneBoard): ScenePanel[] =>
  [0, 1].map((i) => {
    const p = Array.isArray(board.panels) ? board.panels[i] : undefined;
    return p && typeof p === "object" ? p : { items: [] };
  });
const panelItems = (p: ScenePanel) => (Array.isArray(p.items) ? p.items : []).filter((it) => it && typeof it === "object" && it.glyph);

/** "Two scenes to compare: Scene 1, 14 items; Scene 2, 14 items". Counts only: never what differs. */
export function describeScene(board: SceneBoard): string {
  const labels = sceneLabels(board);
  return `Two scenes to compare: ${scenePanels(board)
    .map((p, i) => `${labels[i]}, ${countLabel(panelItems(p).length, "item")}`)
    .join("; ")}`;
}

function SceneItemView({ item, height }: { item: ScenePanel["items"][number]; height: number }) {
  const size = clampNum(item.size, 0.02, 0.3, 0.08) * SCENE_W;
  const x = clampNum(item.x, 0, 1, 0.5) * SCENE_W;
  const y = clampNum(item.y, 0, 1, 0.5) * height;
  const rotation = clampNum(item.rotation, -3600, 3600, 0);
  const g = item.glyph;
  if (kindOf(g) === "text") {
    const t = g as TextGlyph;
    return (
      <text x={x} y={y} fontSize={size} className={styles.sceneText} style={{ fill: puzzleColor(t.color) }} transform={rotation ? `rotate(${rotation} ${x} ${y})` : undefined}>
        {String(t.value ?? "")}
      </text>
    );
  }
  if (kindOf(g) === "shape") {
    const s = g as ShapeGlyph;
    const path = shapePath(s.shape, 46 * Math.min(1, Math.max(0.1, typeof s.scale === "number" ? s.scale : 1)));
    if (!path) return null;
    const color = puzzleColor(s.color);
    const turn = rotation + (typeof s.rotation === "number" ? s.rotation : 0);
    const outline = s.fill === "outline";
    return (
      <g transform={`translate(${x - size / 2} ${y - size / 2}) scale(${size / 100}) rotate(${turn} 50 50)${s.mirrored ? " matrix(-1 0 0 1 100 0)" : ""}`}>
        <path
          d={path.d}
          fillRule={path.evenOdd ? "evenodd" : "nonzero"}
          style={outline ? { fill: "none", stroke: color, strokeWidth: 6 } : { fill: color }}
        />
      </g>
    );
  }
  return (
    <foreignObject x={x - size / 2} y={y - size / 2} width={size} height={size} transform={rotation ? `rotate(${rotation} ${x} ${y})` : undefined}>
      <div className={styles.sceneForeign}>
        <GlyphView glyph={g} />
      </div>
    </foreignObject>
  );
}

/**
 * board.scene@1 (board 18 A1): two pictures to compare, stacked (or side by side for `horizontal`), each a
 * rounded panel with its number badge; items are scatter items (x, y as fractions of the panel, size a
 * fraction of its width). One SVG for both panels. `layer` is the generator's business and not drawn.
 */
export function SceneBoardView({ board }: { board: SceneBoard }) {
  const horizontal = board.layout === "horizontal";
  const panels = scenePanels(board);
  const labels = sceneLabels(board);
  const heights = panels.map((p) => clampNum(p.aspect, 0.5, 2, 0.5) * SCENE_W);
  const width = horizontal ? SCENE_W * 2 + SCENE_GAP : SCENE_W;
  const height = horizontal ? Math.max(...heights) : heights[0]! + SCENE_GAP + heights[1]!;
  const hasLabels = Array.isArray(board.labels) && board.labels.length > 0;
  const idBase = `scene-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <svg className={styles.scene} viewBox={`0 0 ${width} ${height}`} role="group" aria-label={describeScene(board)} data-layout={horizontal ? "horizontal" : "vertical"}>
      {panels.map((p, i) => {
        const h = heights[i]!;
        const ox = horizontal && i === 1 ? SCENE_W + SCENE_GAP : 0;
        const oy = !horizontal && i === 1 ? heights[0]! + SCENE_GAP : 0;
        const clip = `${idBase}-${i}`;
        const items = panelItems(p);
        return (
          <g key={i} transform={`translate(${ox} ${oy})`} role="img" aria-label={`${labels[i]}: ${countLabel(items.length, "item")}`} data-panel={i}>
            <clipPath id={clip}>
              <rect width={SCENE_W} height={h} rx={44} />
            </clipPath>
            <rect width={SCENE_W} height={h} rx={44} className={styles.scenePanel} />
            <g clipPath={`url(#${clip})`}>
              {items.map((it, k) => (
                <SceneItemView key={k} item={it} height={h} />
              ))}
            </g>
            <g aria-hidden>
              <circle cx={44} cy={44} r={24} className={styles.sceneBadge} />
              <text x={44} y={45} className={styles.sceneBadgeText}>
                {i + 1}
              </text>
              {hasLabels ? (
                <text x={80} y={45} className={styles.sceneLabel}>
                  {labels[i]}
                </text>
              ) : null}
            </g>
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------- board.hive@1 and board.wheel@1

const letterList = (v: unknown, n?: number) =>
  (Array.isArray(v) ? v : []).slice(0, n ?? 12).map((l) => String(l ?? "").trim().slice(0, 3));

/** "Letter hive: centre A; around it P, L, N, E, T, S". */
export function describeHive(board: HiveBoard): string {
  return `Letter hive: centre ${String(board.center ?? "").trim()}; around it ${letterList(board.letters, 6).join(", ")}`;
}

/**
 * board.hive@1 (board 20 W3): a flat-topped honeycomb on a card, the centre cell solid accent with dark
 * ink, the six around it on chrome tiles, clockwise from the top; "Centre letter in every word" beneath.
 */
export function HiveBoardView({ board }: { board: HiveBoard }) {
  const { cells, width, height } = hiveLayout();
  const letters = [String(board.center ?? "").trim().slice(0, 3), ...letterList(board.letters, 6)];
  return (
    <div className={styles.wordPanel}>
      <svg className={styles.hive} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={describeHive(board)}>
        {cells.map((c) => (
          <g key={c.index} data-hex={c.index} data-center={c.index === 0 || undefined}>
            <polygon points={c.points} className={c.index === 0 ? styles.hexCenter : styles.hex} />
            <text x={c.x} y={c.y + 1} className={c.index === 0 ? styles.hexCenterText : styles.hexText} fontSize={(letters[c.index]?.length ?? 1) > 1 ? 26 : 34}>
              {letters[c.index] ?? ""}
            </text>
          </g>
        ))}
      </svg>
      <span className={styles.wordCaption}>Centre letter in every word</span>
    </div>
  );
}

/** "Letter wheel: T, R, A, I, N, S; hub ×1". */
export function describeWheel(board: WheelBoard): string {
  return `Letter wheel: ${letterList(board.letters, 9).join(", ")}; hub ${String(board.hub ?? "").trim()}`;
}

/**
 * board.wheel@1 (board 20 W4): letters on discs around a rim, clockwise from the top, the hub a dashed
 * circle with its rule label ("×1"); "Each letter once" beneath when the hub says ×1.
 */
export function WheelBoardView({ board }: { board: WheelBoard }) {
  const letters = letterList(board.letters, 9);
  const size = 300;
  const { rim, hub, letter, spots, c } = wheelLayout(letters.length, size);
  const hubText = String(board.hub ?? "").trim();
  const once = /^[×x]\s*1$/i.test(hubText);
  return (
    <div className={styles.wordPanel}>
      <svg className={styles.wheel} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={describeWheel(board)}>
        <circle cx={c} cy={c} r={rim} className={styles.wheelRim} />
        <circle cx={c} cy={c} r={hub} className={styles.wheelHub} />
        <text x={c} y={c + 1} className={styles.wheelHubText}>
          {hubText}
        </text>
        {spots.map(([x, y], i) => (
          <g key={i} data-letter={i}>
            <circle cx={x} cy={y} r={letter} className={styles.wheelDisc} />
            <text x={x} y={y + 1} className={styles.wheelText} fontSize={letter * ((letters[i]?.length ?? 1) > 1 ? 0.6 : 0.82)}>
              {letters[i]}
            </text>
          </g>
        ))}
      </svg>
      {once ? <span className={styles.wordCaption}>Each letter once</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------- descriptions

/** Accessible names of the launch boards (never the answer), or null for other boards. */
export function describeLaunchBoard(board: Board | undefined): string | null {
  switch (kindOf(board)) {
    case "scene":
      return describeScene(board as SceneBoard);
    case "hive":
      return describeHive(board as HiveBoard);
    case "wheel":
      return describeWheel(board as WheelBoard);
    case "grid": {
      const g = board as GridBoard;
      const rows = clampInt(g.rows, 1, 12, 4);
      const cols = clampInt(g.cols, 1, 12, 4);
      const regions = Array.isArray(g.regions) ? g.regions.filter((r) => r && typeof r === "object") : [];
      if (regions.length) {
        const labelled = regions.some((r) => typeof r.label === "string" && r.label.trim());
        return `Grid, ${rows} by ${cols}, ${countLabel(regions.length, labelled ? "cage" : "region")}`;
      }
      const clues = (Array.isArray(g.cells) ? g.cells : []).filter((c) => isClue(c?.glyph)).length;
      return clues ? `Grid, ${rows} by ${cols}, ${countLabel(clues, "clue square")}` : null;
    }
    default:
      return null;
  }
}
